/**
 * Agentes › Conectar agente — the one screen for plugging an agent into Vephus.
 *
 * It opens on one question, "where will your AI work?", answered with two cards instead of two tabs
 * named after the plumbing (docs/refactor/08-configuracoes.md):
 *
 *  - "In the desktop app", recommended: the AI runs on this computer, every step shows live, and it
 *    takes what is sent from elsewhere. The agents installed here, the desktop key, provider keys,
 *    taking work and Telegram (pages/ConnectAi.tsx). In a browser it leads to the download.
 *  - "Only from your IDE", the reduced way: the MCP server, a key, the instructions and the hooks,
 *    for Claude Code, Cursor, Kiro, OpenCode… (pages/Onboarding.tsx). Works from any browser.
 *
 * The person's own keys stay one link away ("Suas chaves"), the one place to issue, test and revoke them.
 * The choice is in the address (`?tab=desktop` / `?tab=ide` / `?tab=keys`) so a link can open the
 * right one; with none, the desktop app opens — it is where agents run best.
 */

import { useSearchParams } from 'react-router-dom'
import { Code2, KeyRound, MonitorSmartphone } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import Onboarding from './Onboarding'
import ConnectAi from './ConnectAi'
import { MyAgentKeys } from '../components/MyAgentKeys'

export type ConnectTab = 'ide' | 'desktop' | 'keys'

/** The choice a `?tab=` value asks for; with none (or nonsense), the desktop app — the preferred way. */
export function connectTab(requested: string | null): ConnectTab {
  if (requested === 'ide' || requested === 'desktop' || requested === 'keys') return requested
  return 'desktop'
}

export default function ConnectAgent() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const tab = connectTab(params.get('tab'))

  const choose = (next: ConnectTab) => {
    const updated = new URLSearchParams(params)
    updated.set('tab', next)
    setParams(updated, { replace: true })
  }

  const ways: Array<{ value: 'desktop' | 'ide'; icon: typeof Code2; title: string; body: string; recommended?: boolean }> = [
    { value: 'desktop', icon: MonitorSmartphone, title: t('connectAgent.tab.desktop'), body: t('connectAgent.desktopBody'), recommended: true },
    { value: 'ide', icon: Code2, title: t('connectAgent.tab.ide'), body: t('connectAgent.ideBody') },
  ]

  return (
    <div className="stack stack--loose">
      <div className="stack stack--tight">
        <div className="row row--wrap">
          <h2 className="connect-where__title">{t('connectAgent.where')}</h2>
          <div className="spacer" />
          <button
            type="button"
            className={`btn btn--sm${tab === 'keys' ? ' btn--primary' : ' btn--ghost'}`}
            aria-pressed={tab === 'keys'}
            onClick={() => choose('keys')}
          >
            <KeyRound size={13} aria-hidden /> {t('connectAgent.tab.keys')}
          </button>
        </div>
        <div className="connect-where" role="tablist" aria-label={t('connectAgent.where')}>
          {ways.map((way) => (
            <button
              key={way.value}
              type="button"
              role="tab"
              id={`connect-tab-${way.value}`}
              aria-selected={tab === way.value}
              aria-controls="connect-tab-panel"
              className={`connect-where__card${tab === way.value ? ' is-active' : ''}`}
              onClick={() => choose(way.value)}
            >
              <span className="row row--tight">
                <way.icon size={16} aria-hidden />
                <strong>{way.title}</strong>
                {way.recommended && <span className="connect-where__badge">{t('connectAgent.recommended')}</span>}
              </span>
              <span className="small secondary">{way.body}</span>
            </button>
          ))}
        </div>
      </div>

      <div id="connect-tab-panel" role="tabpanel" aria-labelledby={tab === 'keys' ? undefined : `connect-tab-${tab}`} aria-label={tab === 'keys' ? t('connectAgent.tab.keys') : undefined}>
        {tab === 'ide' ? <Onboarding embedded /> : tab === 'desktop' ? <ConnectAi /> : <MyAgentKeys />}
      </div>
    </div>
  )
}
